<template>
  <div class="restaurante-pedidos-online">
    <div class="card card-custom gutter-b">
      <div class="card-header border-0 py-5">
        <h3 class="card-title align-items-start flex-column">
          <span class="card-label font-weight-bolder text-dark">
            Pedidos online
          </span>
          <span class="text-muted mt-2 font-weight-bold font-size-sm">
            Gestão dos pedidos recebidos pelo cardápio online
          </span>
        </h3>
        <div class="card-toolbar">
          <span class="pedidos-refresh-label">
            {{ lastRefreshLabel }}
          </span>
          <button
            class="btn btn-light-dark font-weight-bolder font-size-sm"
            type="button"
            @click="getPedidos"
          >
            Atualizar
          </button>
        </div>
      </div>

      <div class="card-body pt-0">
        <b-row class="align-items-end mb-6">
          <b-col md="3">
            <b-form-group label="Busca" label-for="pedido_busca">
              <b-form-input
                id="pedido_busca"
                v-model="filters.busca"
                placeholder="Código, cliente ou telefone"
                @keyup.enter="getPedidos"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Status" label-for="pedido_status">
              <b-form-select
                id="pedido_status"
                v-model="filters.status"
                :options="statusFilterOptions"
                @change="getPedidos"
              />
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group label="Pagamento" label-for="pedido_pagamento">
              <b-form-select
                id="pedido_pagamento"
                v-model="filters.pagamento_status"
                :options="paymentStatusFilterOptions"
                @change="getPedidos"
              />
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group label="Entrega" label-for="pedido_entrega">
              <b-form-select
                id="pedido_entrega"
                v-model="filters.tipo_entrega"
                :options="deliveryFilterOptions"
                @change="getPedidos"
              />
            </b-form-group>
          </b-col>
          <b-col md="2">
            <button
              class="btn btn-primary btn-block font-weight-bolder"
              type="button"
              @click="getPedidos"
            >
              Filtrar
            </button>
          </b-col>
        </b-row>

        <div class="pedidos-online-resumo">
          <button
            v-for="metric in resumoStatus"
            :key="metric.status || 'todos'"
            type="button"
            :class="[
              'resumo-card',
              { 'resumo-card-active': filters.status === metric.status },
            ]"
            @click="setStatusFilter(metric.status)"
          >
            <span>{{ metric.label }}</span>
            <strong>{{ metric.value }}</strong>
          </button>
        </div>

        <div class="pedidos-online-layout">
          <section class="pedidos-online-lista">
            <div v-if="loadingList" class="empty-state">
              Carregando pedidos online...
            </div>

            <div v-else-if="!pedidos.length" class="empty-state">
              Nenhum pedido online encontrado para os filtros selecionados.
            </div>

            <button
              v-for="pedido in pedidos"
              v-else
              :key="pedidoKey(pedido)"
              type="button"
              :class="[
                'pedido-card',
                {
                  'pedido-card-active':
                    selected && pedidoKey(selected) === pedidoKey(pedido),
                },
              ]"
              @click="selectPedido(pedido)"
            >
              <div class="pedido-card-header">
                <strong>{{ pedidoCodigo(pedido) }}</strong>
                <span :class="statusBadgeClass(pedido.status)">
                  {{ statusLabel(pedido.status) }}
                </span>
              </div>
              <div class="pedido-card-body">
                <span>{{ pedidoCliente(pedido) }}</span>
                <span>{{ formatMoney(pedido.total) }}</span>
              </div>
              <div class="pedido-card-footer">
                <small>{{ deliveryLabel(pedido.tipo_entrega) }}</small>
                <small>{{ formatDate(pedido.created_at) }}</small>
              </div>
            </button>
          </section>

          <section class="pedidos-online-detalhe">
            <div v-if="!selected" class="empty-state">
              Selecione um pedido para ver detalhes, pagamento, histórico e
              ações.
            </div>

            <template v-else>
              <div class="detail-header">
                <div>
                  <span class="text-muted font-weight-bold font-size-sm">
                    Pedido
                  </span>
                  <h4 class="mb-0">{{ pedidoCodigo(selected) }}</h4>
                </div>
                <span :class="statusBadgeClass(selected.status)">
                  {{ statusLabel(selected.status) }}
                </span>
              </div>

              <div class="status-flow">
                <span
                  v-for="step in statusFlow"
                  :key="step.status"
                  :class="[
                    'status-step',
                    {
                      'status-step-done': isStatusStepDone(step.status),
                      'status-step-current': step.status === selected.status,
                    },
                  ]"
                >
                  {{ step.label }}
                </span>
              </div>

              <div class="detail-grid">
                <div>
                  <span>Cliente</span>
                  <strong>{{ selected.cliente_nome || "-" }}</strong>
                </div>
                <div>
                  <span>Telefone</span>
                  <strong>{{ selected.cliente_telefone || "-" }}</strong>
                </div>
                <div>
                  <span>Entrega</span>
                  <strong>{{ deliveryLabel(selected.tipo_entrega) }}</strong>
                </div>
                <div>
                  <span>Total</span>
                  <strong>{{ formatMoney(selected.total) }}</strong>
                </div>
              </div>

              <div class="detail-section">
                <h5>Pagamento</h5>
                <div class="detail-grid">
                  <div>
                    <span>Método</span>
                    <strong>{{ paymentMethodLabel(payment.metodo) }}</strong>
                  </div>
                  <div>
                    <span>Status</span>
                    <strong>{{ paymentStatusLabel(payment.status) }}</strong>
                  </div>
                  <div>
                    <span>Valor</span>
                    <strong>{{ formatMoney(payment.valor) }}</strong>
                  </div>
                  <div>
                    <span>Gateway</span>
                    <strong>{{ payment.gateway || "-" }}</strong>
                  </div>
                  <div v-if="payment.troco_para">
                    <span>Troco para</span>
                    <strong>{{ formatMoney(payment.troco_para) }}</strong>
                  </div>
                  <div v-if="payment.troco">
                    <span>Troco</span>
                    <strong>{{ formatMoney(payment.troco) }}</strong>
                  </div>
                </div>
                <div v-if="paymentPending" class="payment-attention mt-4">
                  <div>
                    <strong>Cobrança pendente</strong>
                    <span>Registre a baixa antes de concluir o pedido.</span>
                  </div>
                  <button
                    class="btn btn-sm btn-warning font-weight-bolder"
                    type="button"
                    :disabled="actionLoading"
                    @click="openChargeModal"
                  >
                    Abrir cobrança
                  </button>
                </div>
              </div>

              <div class="detail-section">
                <h5>Atendimento</h5>
                <div class="detail-grid">
                  <div>
                    <span>Tipo</span>
                    <strong>{{ deliveryLabel(selected.tipo_entrega) }}</strong>
                  </div>
                  <div>
                    <span>Endereço</span>
                    <strong>{{ enderecoResumo(selected.endereco) }}</strong>
                  </div>
                  <div>
                    <span>Documento</span>
                    <strong>{{ selected.cliente_documento || "-" }}</strong>
                  </div>
                  <div>
                    <span>Observação</span>
                    <strong>{{ selected.observacao || "-" }}</strong>
                  </div>
                </div>
              </div>

              <div class="detail-section">
                <h5>Itens</h5>
                <div v-if="!itens.length" class="empty-state empty-state-small">
                  Nenhum item retornado pela API.
                </div>
                <div
                  v-for="item in itens"
                  :key="itemKey(item)"
                  class="item-row"
                >
                  <div>
                    <strong>{{
                      item.nome_produto || item.nome || item.produto || "Item"
                    }}</strong>
                    <span>{{ itemResumo(item) }}</span>
                    <ul v-if="itemDetalhes(item).length" class="item-details">
                      <li
                        v-for="(detail, detailIndex) in itemDetalhes(item)"
                        :key="detailIndex"
                      >
                        {{ detail }}
                      </li>
                    </ul>
                  </div>
                  <div>
                    <span>{{ item.quantidade || 1 }}x</span>
                    <strong>{{ formatMoney(item.valor_total) }}</strong>
                  </div>
                </div>
              </div>

              <div class="detail-section">
                <h5>Histórico</h5>
                <div
                  v-if="!historicos.length"
                  class="empty-state empty-state-small"
                >
                  Sem histórico retornado pela API.
                </div>
                <div
                  v-for="historico in historicos"
                  :key="historicoKey(historico)"
                  class="history-row"
                >
                  <span>{{ formatDate(historico.created_at) }}</span>
                  <strong>
                    {{ statusLabel(historico.status_para || historico.status) }}
                  </strong>
                  <small>{{
                    historico.observacao || historico.origem || "-"
                  }}</small>
                </div>
              </div>

              <div class="detail-section">
                <h5>Ações operacionais</h5>
                <div class="actions-panel">
                  <button
                    v-if="canAcceptSelected"
                    class="btn btn-success font-weight-bolder"
                    type="button"
                    :disabled="actionLoading"
                    @click="aceitarPedido"
                  >
                    Aceitar e enviar para produção
                  </button>
                  <button
                    v-if="canStartProductionSelected"
                    class="btn btn-primary font-weight-bolder"
                    type="button"
                    :disabled="actionLoading"
                    @click="alterarStatus('em_preparo')"
                  >
                    Enviar para produção
                  </button>
                  <button
                    v-if="canRefuseSelected"
                    class="btn btn-light-danger font-weight-bolder"
                    type="button"
                    :disabled="actionLoading"
                    @click="recusarPedido"
                  >
                    Recusar
                  </button>
                  <button
                    v-if="canCancelSelected"
                    class="btn btn-light-danger font-weight-bolder"
                    type="button"
                    :disabled="actionLoading"
                    @click="cancelarPedido"
                  >
                    Cancelar
                  </button>
                  <button
                    v-if="canDispatchSelected"
                    class="btn btn-primary font-weight-bolder"
                    type="button"
                    :disabled="actionLoading"
                    @click="alterarStatus('saiu_para_entrega')"
                  >
                    Saiu para entrega
                  </button>
                  <button
                    v-if="canFinishPickupSelected"
                    class="btn btn-primary font-weight-bolder"
                    type="button"
                    :disabled="actionLoading"
                    @click="alterarStatus('entregue')"
                  >
                    Registrar retirada
                  </button>
                  <button
                    v-if="canDeliverSelected"
                    class="btn btn-success font-weight-bolder"
                    type="button"
                    :disabled="actionLoading"
                    @click="alterarStatus('entregue')"
                  >
                    Marcar entregue
                  </button>
                  <button
                    v-if="canOpenChargeSelected"
                    class="btn btn-warning font-weight-bolder"
                    type="button"
                    :disabled="actionLoading"
                    @click="openChargeModal"
                  >
                    Abrir cobrança
                  </button>
                  <button
                    v-if="canConcludeStatusSelected"
                    class="btn btn-light-success font-weight-bolder"
                    type="button"
                    :disabled="actionLoading"
                    @click="concluirPedido"
                  >
                    Concluir pedido
                  </button>
                  <button
                    class="btn btn-light-dark font-weight-bolder"
                    type="button"
                    :disabled="actionLoading"
                    @click="imprimirPedido('checkout')"
                  >
                    Imprimir pedido/checkout
                  </button>
                  <button
                    class="btn btn-light-info font-weight-bolder"
                    type="button"
                    :disabled="actionLoading || paymentPending"
                    @click="imprimirPedido('comprovante')"
                  >
                    Imprimir comprovante
                  </button>
                  <div v-if="!contextualActions.length" class="actions-empty">
                    Nenhuma ação manual disponível para este status.
                  </div>
                </div>
              </div>
            </template>
          </section>
        </div>
      </div>
    </div>

    <b-modal
      id="modal-pedido-online-cobranca"
      title="Cobrança do pedido online"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <div v-if="selected" class="online-charge-modal">
        <div class="online-charge-summary">
          <div>
            <span>Pedido</span>
            <strong>{{ pedidoCodigo(selected) }}</strong>
          </div>
          <div>
            <span>Cliente</span>
            <strong>{{ pedidoCliente(selected) }}</strong>
          </div>
          <div>
            <span>Forma</span>
            <strong>{{ paymentMethodLabel(payment.metodo) }}</strong>
          </div>
          <div>
            <span>Total</span>
            <strong>{{ formatMoney(payment.valor || selected.total) }}</strong>
          </div>
        </div>

        <div v-if="isPixPayment" class="alert alert-warning mt-5">
          Pix manual exige conferência fora do cardápio online. O histórico do
          pedido registrará usuário, horário e observação da baixa.
        </div>

        <b-row class="align-items-end mt-5">
          <b-col md="6">
            <b-form-group label="Valor recebido">
              <b-form-input
                v-model="chargeForm.valor_recebido"
                inputmode="decimal"
                placeholder="Ex.: 40,00"
                :disabled="isCardPayment || isPixPayment"
              />
              <small class="text-muted">
                Para dinheiro, informe o valor recebido para calcular troco.
              </small>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <div class="online-charge-change">
              <span>Troco calculado</span>
              <strong>{{ formatMoney(chargeChange) }}</strong>
            </div>
          </b-col>
        </b-row>

        <b-form-group label="Observação da baixa">
          <b-form-textarea
            v-model="chargeForm.observacao"
            rows="3"
            maxlength="255"
            placeholder="Ex.: recebido no balcão, recebido pelo entregador, comprovante conferido."
          />
        </b-form-group>

        <b-form-checkbox
          v-if="isPixPayment"
          v-model="chargeForm.confirmar_pix_manual"
          class="mb-5"
        >
          Confirmo que o Pix foi conferido manualmente e autorizo a baixa.
        </b-form-checkbox>

        <div class="online-charge-footer">
          <button
            class="btn btn-light font-weight-bolder"
            type="button"
            :disabled="actionLoading"
            @click="$bvModal.hide('modal-pedido-online-cobranca')"
          >
            Cancelar
          </button>
          <button
            class="btn btn-success font-weight-bolder"
            type="button"
            :disabled="actionLoading || !canSubmitCharge"
            @click="registrarPagamento"
          >
            {{ actionLoading ? "Registrando..." : "Registrar pagamento" }}
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

const STATUS_OPTIONS = [
  { value: "aguardando_pagamento", text: "Aguardando pagamento" },
  { value: "pagamento_expirado", text: "Pagamento expirado" },
  { value: "aguardando_aceite", text: "Aguardando aceite" },
  { value: "aceito", text: "Aceito" },
  { value: "recebido", text: "Recebido" },
  { value: "em_preparo", text: "Em preparo" },
  { value: "pronto", text: "Pronto" },
  { value: "saiu_para_entrega", text: "Saiu para entrega" },
  { value: "entregue", text: "Entregue" },
  { value: "concluido", text: "Concluído" },
  { value: "cancelado", text: "Cancelado" },
  { value: "recusado", text: "Recusado" },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: "pendente", text: "Pendente" },
  { value: "pago", text: "Pago" },
  { value: "expirado", text: "Expirado" },
  { value: "cancelado", text: "Cancelado" },
  { value: "falhou", text: "Falhou" },
];

const REFRESH_INTERVAL_MS = 15000;

export default {
  name: "app-restaurante-pedidos-online",
  data() {
    return {
      pedidos: [],
      selected: null,
      loadingList: false,
      actionLoading: false,
      refreshInterval: null,
      lastRefreshAt: null,
      filters: {
        busca: "",
        status: null,
        pagamento_status: null,
        tipo_entrega: null,
      },
      chargeForm: {
        valor_recebido: "",
        observacao: "",
        confirmar_pix_manual: false,
      },
    };
  },
  computed: {
    ...mapGetters(["itsblocked"]),
    statusFilterOptions() {
      return [{ value: null, text: "Todos" }, ...STATUS_OPTIONS];
    },
    statusActionOptions() {
      return [
        { value: null, text: "Selecione o próximo status" },
        ...STATUS_OPTIONS,
      ];
    },
    paymentStatusFilterOptions() {
      return [{ value: null, text: "Todos" }, ...PAYMENT_STATUS_OPTIONS];
    },
    deliveryFilterOptions() {
      return [
        { value: null, text: "Todos" },
        { value: "delivery", text: "Entrega" },
        { value: "pickup", text: "Retirada" },
      ];
    },
    payment() {
      if (!this.selected) {
        return {};
      }

      if (this.selected.pagamento) {
        return this.selected.pagamento;
      }

      if (
        Array.isArray(this.selected.pagamentos) &&
        this.selected.pagamentos.length
      ) {
        return this.selected.pagamentos[0];
      }

      return {};
    },
    itens() {
      if (!this.selected) {
        return [];
      }

      return this.selected.itens || this.selected.items || [];
    },
    historicos() {
      if (!this.selected) {
        return [];
      }

      return this.selected.historicos || this.selected.historico || [];
    },
    resumoStatus() {
      const count = (status) =>
        status
          ? this.pedidos.filter((pedido) => pedido.status === status).length
          : this.pedidos.length;

      return [
        { status: null, label: "Todos", value: count(null) },
        {
          status: "aguardando_aceite",
          label: "Aceite",
          value: count("aguardando_aceite") + count("recebido"),
        },
        { status: "em_preparo", label: "Produção", value: count("em_preparo") },
        { status: "pronto", label: "Prontos", value: count("pronto") },
        {
          status: "saiu_para_entrega",
          label: "Em rota",
          value: count("saiu_para_entrega"),
        },
      ];
    },
    statusFlow() {
      return [
        { status: "recebido", label: "Recebido" },
        { status: "aceito", label: "Aceito" },
        { status: "em_preparo", label: "Produção" },
        { status: "pronto", label: "Pronto" },
        {
          status:
            this.selected?.tipo_entrega === "delivery"
              ? "saiu_para_entrega"
              : "entregue",
          label:
            this.selected?.tipo_entrega === "delivery" ? "Em rota" : "Retirado",
        },
        { status: "concluido", label: "Concluído" },
      ];
    },
    currentStatusFlowIndex() {
      if (!this.selected) {
        return -1;
      }

      return this.statusFlowIndex(this.selected.status);
    },
    canAcceptSelected() {
      return ["recebido", "aguardando_aceite"].includes(this.selected?.status);
    },
    canRefuseSelected() {
      return ["recebido", "aguardando_aceite"].includes(this.selected?.status);
    },
    canStartProductionSelected() {
      return this.selected?.status === "aceito";
    },
    canCancelSelected() {
      return (
        this.selected &&
        !["concluido", "cancelado", "recusado", "pagamento_expirado"].includes(
          this.selected.status
        )
      );
    },
    canDispatchSelected() {
      return (
        this.selected?.status === "pronto" &&
        this.selected?.tipo_entrega === "delivery"
      );
    },
    canFinishPickupSelected() {
      return (
        this.selected?.status === "pronto" &&
        this.selected?.tipo_entrega === "pickup"
      );
    },
    canDeliverSelected() {
      return this.selected?.status === "saiu_para_entrega";
    },
    paymentPending() {
      return Boolean(
        this.payment?.status &&
          !["pago", "cancelado", "expirado", "falhou"].includes(
            this.payment.status
          )
      );
    },
    isPixPayment() {
      return this.payment?.metodo === "pix";
    },
    isCardPayment() {
      return this.payment?.metodo === "card_on_delivery";
    },
    isCashPayment() {
      return this.payment?.metodo === "cash_on_delivery";
    },
    canOpenChargeSelected() {
      return Boolean(
        this.selected &&
          this.payment?.id &&
          this.paymentPending &&
          ![
            "cancelado",
            "recusado",
            "pagamento_expirado",
            "concluido",
          ].includes(this.selected.status)
      );
    },
    canConcludeStatusSelected() {
      return this.selected?.status === "entregue";
    },
    canConcludeSelected() {
      return this.canConcludeStatusSelected && !this.paymentPending;
    },
    chargePaymentValue() {
      return Number(this.payment?.valor || this.selected?.total || 0);
    },
    chargeReceivedValue() {
      return this.parseMoneyValue(this.chargeForm.valor_recebido);
    },
    chargeChange() {
      if (!this.isCashPayment) {
        return 0;
      }

      return Math.max(0, this.chargeReceivedValue - this.chargePaymentValue);
    },
    canSubmitCharge() {
      if (!this.canOpenChargeSelected) {
        return false;
      }

      if (this.isPixPayment && !this.chargeForm.confirmar_pix_manual) {
        return false;
      }

      if (this.isCashPayment) {
        return this.chargeReceivedValue >= this.chargePaymentValue;
      }

      return true;
    },
    contextualActions() {
      return [
        this.canAcceptSelected,
        this.canRefuseSelected,
        this.canStartProductionSelected,
        this.canCancelSelected,
        this.canDispatchSelected,
        this.canFinishPickupSelected,
        this.canDeliverSelected,
        this.canOpenChargeSelected,
        this.canConcludeStatusSelected,
      ].filter(Boolean);
    },
    lastRefreshLabel() {
      if (!this.lastRefreshAt) {
        return "Aguardando atualização";
      }

      return `Atualizado ${this.lastRefreshAt.toLocaleTimeString("pt-BR")}`;
    },
  },
  async mounted() {
    if (this.itsblocked) {
      this.$router.push({ name: "assinatura" });
      return;
    }

    const ativo = await this.ensureCardapioOnlineAtivo();
    if (!ativo) {
      return;
    }

    this.getPedidos();
    this.refreshInterval = setInterval(() => {
      if (!this.actionLoading) {
        this.getPedidos(false);
      }
    }, REFRESH_INTERVAL_MS);
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  methods: {
    ...mapActions(["setLoading"]),
    async ensureCardapioOnlineAtivo() {
      try {
        const { data } = await this.$http.post("/configuracoesgerais/find");
        const configuracao = Array.isArray(data) ? data[0] : data;

        if (configuracao?.restaurante_cardapio_online_ativo) {
          return true;
        }
      } catch (error) {
        this.$toast.error(
          "Não foi possível validar a configuração do Cardapio Online."
        );
      }

      this.$toast.warning(
        "Ative o Cardapio Online em Configurações > Restaurante para acessar os pedidos online."
      );
      this.$router.push("/gerais");
      return false;
    },
    async getPedidos(showLoading = true) {
      if (showLoading) {
        this.loadingList = true;
      }

      try {
        const { data } = await this.$http.get("/restaurante/pedidos-online", {
          params: this.cleanFilters(),
        });

        this.pedidos = this.normalizeList(data);

        if (this.selected) {
          const selectedKey = this.pedidoKey(this.selected);
          const pedidoAtualizado = this.pedidos.find(
            (pedido) => this.pedidoKey(pedido) === selectedKey
          );

          if (pedidoAtualizado) {
            this.selected = {
              ...this.selected,
              ...pedidoAtualizado,
            };
          }

          await this.refreshSelectedPedido(false);
        }

        this.lastRefreshAt = new Date();
      } catch (error) {
        if (showLoading) {
          this.pedidos = [];
          this.$toast.error(
            this.apiErrorMessage(
              error,
              "Não foi possível carregar os pedidos online."
            )
          );
        }
      } finally {
        if (showLoading) {
          this.loadingList = false;
        }
      }
    },
    async selectPedido(pedido) {
      this.selected = pedido;

      await this.refreshSelectedPedido(true);
    },
    async refreshSelectedPedido(showLoading = true) {
      if (!this.selected) {
        return;
      }

      const pedido = this.selected;
      const id = this.pedidoId(pedido);

      if (!id) {
        return;
      }

      try {
        if (showLoading) {
          this.setLoading(true);
        }
        const { data } = await this.$http.get(
          `/restaurante/pedidos-online/${id}`
        );
        this.selected = data.data || data.pedido || data;
      } catch (error) {
        this.$toast.warning(
          this.apiErrorMessage(
            error,
            "Não foi possível carregar o detalhe completo do pedido."
          )
        );
      } finally {
        if (showLoading) {
          this.setLoading(false);
        }
      }
    },
    async aceitarPedido() {
      await this.executarAcao("aceitar", "Pedido aceito.");
    },
    async recusarPedido() {
      const result = await this.$swal.fire({
        icon: "warning",
        title: "Recusar pedido online?",
        input: "text",
        inputPlaceholder: "Motivo opcional",
        showCancelButton: true,
        confirmButtonText: "Sim",
        cancelButtonText: "Não",
        confirmButtonColor: "#f64e60",
      });

      if (!result.isConfirmed) {
        return;
      }

      await this.executarAcao("recusar", "Pedido recusado.", {
        motivo: result.value || null,
      });
    },
    async cancelarPedido() {
      const result = await this.$swal.fire({
        icon: "warning",
        title: "Cancelar pedido online?",
        input: "text",
        inputPlaceholder: "Informe o motivo do cancelamento",
        inputValidator: (value) =>
          value && value.trim()
            ? undefined
            : "Informe o motivo do cancelamento.",
        showCancelButton: true,
        confirmButtonText: "Cancelar pedido",
        cancelButtonText: "Voltar",
        confirmButtonColor: "#f64e60",
      });

      if (!result.isConfirmed) {
        return;
      }

      await this.alterarStatus("cancelado", result.value);
    },
    async alterarStatus(status, motivo = null) {
      if (!status) {
        return;
      }

      if (status === "concluido" && this.paymentPending) {
        this.$toast.warning("Registre a cobrança antes de concluir o pedido.");
        this.openChargeModal();
        return;
      }

      await this.executarAcao("status", "Status atualizado.", {
        status,
        motivo,
      });
    },
    async concluirPedido() {
      await this.alterarStatus("concluido");
    },
    openChargeModal() {
      if (!this.canOpenChargeSelected) {
        this.$toast.warning(
          "Este pedido não possui cobrança pendente para baixa."
        );
        return;
      }

      this.chargeForm = {
        valor_recebido: this.moneyInputValue(
          this.payment.valor || this.selected.total
        ),
        observacao: "",
        confirmar_pix_manual: false,
      };
      this.$bvModal.show("modal-pedido-online-cobranca");
    },
    async registrarPagamento() {
      if (!this.canSubmitCharge || !this.selected) {
        return;
      }

      const id = this.pedidoId(this.selected);
      const payload = {
        valor_recebido: this.isCashPayment
          ? this.chargeReceivedValue
          : this.chargePaymentValue,
        observacao: this.chargeForm.observacao || null,
        confirmar_pix_manual: this.isPixPayment
          ? this.chargeForm.confirmar_pix_manual
          : false,
      };

      this.actionLoading = true;

      try {
        const { data } = await this.$http.post(
          `/restaurante/pedidos-online/${id}/pagamento/baixar`,
          payload
        );
        this.selected = data.data || data.pedido || data;
        this.$bvModal.hide("modal-pedido-online-cobranca");
        this.$toast.success("Pagamento registrado.");
        this.$bvModal
          .msgBoxConfirm("Deseja abrir o comprovante operacional agora?", {
            title: "Imprimir comprovante",
            okTitle: "Abrir comprovante",
            cancelTitle: "Agora não",
            okVariant: "primary",
            cancelVariant: "light",
          })
          .then((confirmado) => {
            if (confirmado) {
              this.imprimirPedido("comprovante");
            }
          });
        await this.getPedidos(false);
      } catch (error) {
        this.$toast.error(
          this.apiErrorMessage(error, "Não foi possível registrar o pagamento.")
        );
      } finally {
        this.actionLoading = false;
      }
    },
    setStatusFilter(status) {
      this.filters.status = status;
      this.getPedidos();
    },
    async executarAcao(action, successMessage, payload = {}) {
      if (!this.selected) {
        return;
      }

      const id = this.pedidoId(this.selected);

      if (!id) {
        this.$toast.error("Pedido sem identificador interno.");
        return;
      }

      this.actionLoading = true;

      try {
        const { data } = await this.$http.post(
          `/restaurante/pedidos-online/${id}/${action}`,
          payload
        );

        this.selected = data.data || data.pedido || this.selected;
        this.$toast.success(successMessage);
        await this.getPedidos();
      } catch (error) {
        this.$toast.error(
          this.apiErrorMessage(
            error,
            "Não foi possível concluir a ação do pedido online."
          )
        );
      } finally {
        this.actionLoading = false;
      }
    },
    async imprimirPedido(tipo = "checkout") {
      if (!this.selected) {
        return;
      }

      const id = this.pedidoId(this.selected);

      if (!id) {
        this.$toast.error("Pedido sem identificador interno.");
        return;
      }

      try {
        this.actionLoading = true;
        const { data } = await this.$http.get(
          `/restaurante/pedidos-online/${id}/impressao`,
          { params: { tipo } }
        );
        const url =
          data.url_impressao ||
          data.url ||
          data.impressao_url ||
          data.impressao;

        if (url) {
          window.open(url, "_blank");
          return;
        }

        if (typeof data === "string" && data.trim().startsWith("<")) {
          const janela = window.open("", "_blank");
          janela.document.write(data);
          janela.document.close();
          return;
        }

        this.$toast.warning("A impressão Blade não retornou uma URL válida.");
      } catch (error) {
        this.$toast.error(
          this.apiErrorMessage(
            error,
            "Não foi possível abrir a impressão Blade do pedido."
          )
        );
      } finally {
        this.actionLoading = false;
      }
    },
    cleanFilters() {
      return Object.entries(this.filters).reduce((params, [key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          params[key] = value;
        }

        return params;
      }, {});
    },
    normalizeList(data) {
      if (Array.isArray(data)) {
        return data;
      }

      if (Array.isArray(data.data)) {
        return data.data;
      }

      if (Array.isArray(data.pedidos)) {
        return data.pedidos;
      }

      if (Array.isArray(data.items)) {
        return data.items;
      }

      return [];
    },
    apiErrorMessage(error, fallback) {
      const response = error?.response;
      const data = response?.data;

      if (typeof data === "string") {
        return data;
      }

      if (data?.msg) {
        return data.msg;
      }

      if (data?.message) {
        return data.message;
      }

      if (response?.status === 401) {
        return "Sessão expirada. Faça login novamente para carregar os pedidos online.";
      }

      if (response?.status === 404) {
        return "Rota de pedidos online não encontrada no backend. Recarregue o servidor Laravel/Swoole.";
      }

      return fallback;
    },
    pedidoId(pedido) {
      return (
        pedido.id ||
        pedido.pedido_online_id ||
        pedido.codigo_publico ||
        pedido.codigo
      );
    },
    pedidoKey(pedido) {
      return this.pedidoId(pedido) || JSON.stringify(pedido);
    },
    pedidoCodigo(pedido) {
      return (
        pedido.codigo_publico || pedido.codigo || pedido.code || `#${pedido.id}`
      );
    },
    pedidoCliente(pedido) {
      return (
        pedido.cliente_nome || pedido.cliente?.nome || "Cliente não informado"
      );
    },
    itemKey(item) {
      return (
        item.id || item.codigo_produto || item.produto || JSON.stringify(item)
      );
    },
    itemResumo(item) {
      const quantidade = Number(item.quantidade || 1).toLocaleString("pt-BR");
      const adicionais = Array.isArray(item.adicionais)
        ? item.adicionais.length
        : 0;

      if (item.pizza) {
        const tamanho = item.pizza?.tamanho?.nome || "Pizza";
        const sabores = (item.pizza?.sabores || [])
          .map((sabor) => sabor.nome)
          .filter(Boolean)
          .join(", ");

        return sabores
          ? `${quantidade}x ${tamanho} - ${sabores}`
          : `${quantidade}x ${tamanho}`;
      }

      return adicionais
        ? `${quantidade}x com ${adicionais} adicional(is)`
        : `${quantidade}x`;
    },
    itemDetalhes(item) {
      const detalhes = [];

      if (item.pizza) {
        const fracao = item.pizza.fracao ? ` (${item.pizza.fracao})` : "";
        const sabores = (item.pizza.sabores || [])
          .map((sabor) => sabor.nome)
          .filter(Boolean)
          .join(", ");

        if (item.pizza?.tamanho?.nome) {
          detalhes.push(`Tamanho: ${item.pizza.tamanho.nome}${fracao}`);
        }

        if (sabores) {
          detalhes.push(`Sabores: ${sabores}`);
        }
      }

      if (Array.isArray(item.adicionais) && item.adicionais.length) {
        detalhes.push(
          `Adicionais: ${item.adicionais
            .map((adicional) => adicional.nome_opcao || adicional.nome)
            .filter(Boolean)
            .join(", ")}`
        );
      }

      if (item.observacao) {
        detalhes.push(`Observação: ${item.observacao}`);
      }

      return detalhes;
    },
    historicoKey(historico) {
      return `${historico.created_at || ""}:${
        historico.status_para || historico.status || ""
      }`;
    },
    statusFlowIndex(status) {
      if (!status) {
        return -1;
      }

      const index = this.statusFlow.findIndex((step) => step.status === status);

      if (status === "aguardando_aceite") {
        return 0;
      }

      return index;
    },
    isStatusStepDone(status) {
      return this.statusFlowIndex(status) <= this.currentStatusFlowIndex;
    },
    statusLabel(status) {
      const option = STATUS_OPTIONS.find((item) => item.value === status);
      return option ? option.text : status || "-";
    },
    paymentStatusLabel(status) {
      const option = PAYMENT_STATUS_OPTIONS.find(
        (item) => item.value === status
      );
      return option ? option.text : status || "-";
    },
    paymentMethodLabel(method) {
      const labels = {
        pix: "Pix",
        cash_on_delivery: "Dinheiro na entrega",
        card_on_delivery: "Cartão na entrega",
      };

      return labels[method] || method || "-";
    },
    deliveryLabel(tipo) {
      const labels = {
        delivery: "Entrega",
        pickup: "Retirada",
      };

      return labels[tipo] || tipo || "-";
    },
    enderecoResumo(endereco) {
      if (!endereco) {
        return this.selected?.tipo_entrega === "pickup"
          ? "Retirada no balcão"
          : "-";
      }

      const linha = [
        endereco.logradouro,
        endereco.numero ? `nº ${endereco.numero}` : null,
        endereco.complemento,
      ]
        .filter(Boolean)
        .join(", ");
      const bairroCidade = [endereco.bairro, endereco.cidade, endereco.uf]
        .filter(Boolean)
        .join(" - ");
      const referencia = endereco.referencia
        ? `Ref.: ${endereco.referencia}`
        : null;

      return [linha, bairroCidade, referencia].filter(Boolean).join(" | ");
    },
    statusBadgeClass(status) {
      const tones = {
        aguardando_pagamento: "label-light-warning",
        pagamento_expirado: "label-light-danger",
        aguardando_aceite: "label-light-warning",
        aceito: "label-light-primary",
        recebido: "label-light-primary",
        em_preparo: "label-light-info",
        pronto: "label-light-success",
        saiu_para_entrega: "label-light-success",
        entregue: "label-light-success",
        cancelado: "label-light-danger",
        recusado: "label-light-danger",
      };

      return [
        "label",
        "label-lg",
        "label-inline",
        tones[status] || "label-light-dark",
      ];
    },
    parseMoneyValue(value) {
      if (typeof value === "number") {
        return value;
      }

      const normalized = String(value || "")
        .replace(/[^0-9,.-]/g, "")
        .replace(/\./g, "")
        .replace(",", ".");
      const number = Number(normalized);

      return Number.isFinite(number) ? number : 0;
    },
    moneyInputValue(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatMoney(value) {
      const number = Number(value || 0);

      return number.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.pedidos-online-layout {
  display: grid;
  gap: 20px;
}

.pedidos-online-resumo {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin-bottom: 20px;
}

.resumo-card {
  background: #f8f9fb;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  color: #3f4254;
  padding: 14px;
  text-align: left;
}

.resumo-card span {
  display: block;
  color: #7e8299;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.resumo-card strong {
  display: block;
  color: #181c32;
  font-size: 24px;
  margin-top: 4px;
}

.resumo-card-active {
  background: #eef6ff;
  border-color: #3699ff;
}

.pedidos-online-lista,
.pedidos-online-detalhe {
  min-width: 0;
}

.pedidos-refresh-label {
  color: #7e8299;
  font-size: 12px;
  font-weight: 800;
  margin-right: 12px;
  text-transform: uppercase;
}

.pedido-card {
  display: block;
  width: 100%;
  margin-bottom: 12px;
  padding: 16px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  text-align: left;
}

.pedido-card-active {
  border-color: #3699ff;
  box-shadow: 0 0 0 2px rgba(54, 153, 255, 0.12);
}

.pedido-card-header,
.pedido-card-body,
.pedido-card-footer,
.detail-header,
.item-row,
.history-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.pedido-card-header,
.detail-header {
  align-items: center;
}

.pedido-card-body {
  margin-top: 10px;
  color: #3f4254;
  font-weight: 600;
}

.pedido-card-footer {
  margin-top: 8px;
  color: #7e8299;
}

.detail-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 16px;
}

.detail-grid div {
  min-width: 0;
  padding: 12px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #f8f9fb;
}

.detail-grid span {
  display: block;
  color: #7e8299;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.detail-grid strong {
  display: block;
  margin-top: 4px;
  color: #181c32;
}

.status-flow {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin-top: 18px;
}

.status-step {
  background: #f3f6f9;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  color: #7e8299;
  font-size: 12px;
  font-weight: 800;
  padding: 9px 10px;
  text-align: center;
  text-transform: uppercase;
}

.status-step-done {
  background: #e8fff3;
  border-color: #b6f4d0;
  color: #0bb783;
}

.status-step-current {
  box-shadow: inset 0 -3px 0 #0bb783;
}

.detail-section {
  margin-top: 24px;
}

.detail-section h5 {
  margin-bottom: 12px;
  color: #181c32;
  font-weight: 800;
}

.item-row,
.history-row {
  padding: 12px 0;
  border-bottom: 1px solid #ebedf3;
}

.item-row:last-child,
.history-row:last-child {
  border-bottom: 0;
}

.item-row span,
.history-row span,
.history-row small {
  display: block;
  color: #7e8299;
}

.item-details {
  color: #3f4254;
  font-weight: 600;
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
}

.item-details li + li {
  margin-top: 3px;
}

.actions-panel {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.actions-empty {
  align-items: center;
  background: #f8f9fb;
  border: 1px dashed #d1d3e0;
  border-radius: 6px;
  color: #7e8299;
  display: flex;
  font-weight: 700;
  min-height: 42px;
  padding: 10px 12px;
}

.empty-state {
  padding: 24px;
  border: 1px dashed #d1d3e0;
  border-radius: 6px;
  color: #7e8299;
  font-weight: 700;
  text-align: center;
}

.empty-state-small {
  padding: 14px;
}

@media (min-width: 992px) {
  .pedidos-online-layout {
    grid-template-columns: minmax(320px, 420px) 1fr;
  }
}

@media (max-width: 767px) {
  .pedidos-online-resumo,
  .status-flow,
  .detail-grid,
  .actions-panel {
    grid-template-columns: 1fr;
  }
}

.payment-attention {
  align-items: center;
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  padding: 14px 16px;
}

.payment-attention div,
.online-charge-summary div,
.online-charge-change {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.payment-attention span,
.online-charge-summary span,
.online-charge-change span {
  color: #7e8299;
  font-size: 12px;
  font-weight: 600;
}

.online-charge-summary {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.online-charge-summary div,
.online-charge-change {
  background: #f7f8fb;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  padding: 14px;
}

.online-charge-summary strong,
.online-charge-change strong {
  color: #181c32;
  font-size: 16px;
}

.online-charge-footer {
  border-top: 1px solid #e4e6ef;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 18px;
}

@media (max-width: 768px) {
  .payment-attention,
  .online-charge-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .online-charge-summary {
    grid-template-columns: 1fr;
  }
}
</style>
